import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Box,
    Typography,
    Avatar
} from '@mui/material';
import 'react-quill/dist/quill.snow.css';
import PropTypes from 'prop-types';
import MDEditor from '@uiw/react-md-editor';
import LoadingPage from '../pages/LoadingPage';
import { useTranslation } from 'react-i18next';

const ProductForm = ({ open, onClose, onSubmit, uploading, successMessage, user, uploadLoading, uploadError, errorMessage, mode, product: existingProduct }) => {
    const { t, i18n } = useTranslation();
    const [product, setProduct] = useState({
        product_name: '',
        product_description: '',
        product_image: null
    });
    const [preview, setPreview] = useState(mode === "edit" ? existingProduct?.product_image  : '');
    const [uploadErrorMessage, setUploadErrorMessage] = useState('');
    useEffect(() => {
        if (mode === "edit") {
            setProduct({
                product_id: existingProduct?.product_id,
                product_name: existingProduct?.product_name || '',
                product_description: existingProduct?.product_description || '',
                product_image: existingProduct?.product_image || null
            });
            setPreview(existingProduct?.product_image || '');
        } else {
            setProduct({
                product_name: '',
                product_description: '',
                product_image: null
            });
            setPreview('');
        }
    }, [mode, existingProduct]);
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                setUploadErrorMessage('Image size should be less than 2MB');
                return;
            }
            setProduct({ ...product, product_image: file });
            setPreview(URL.createObjectURL(file));
            setUploadErrorMessage('');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (mode === "edit") {
            if (!product.product_name || !product.product_description) {
                setUploadErrorMessage(t('fillRequiredFields'));
                return;
            }

            const updateData = {
                product_id: product.product_id,
                product_name: product.product_name,
                product_description: product.product_description,
                product_image: product.product_image
            };

            onSubmit(updateData);
        } else {
            if (!product.product_name || !product.product_description || !product.product_image) {
                setUploadErrorMessage('Please fill all fields');
                return;
            }
            setUploadErrorMessage('');
            onSubmit({
                ...product,
                userId: user.id,
                role: user.role
            });
        }
        setProduct({
            product_name: '',
            product_description: '',
            product_image: null
        });
        setPreview('');

        
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir={i18n.dir()}>
            <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
                {mode === 'edit' ? t('EditProduct') : t('AddNewProduct')}
            </DialogTitle>
            {uploadLoading ? (<LoadingPage />) :
                <DialogContent sx={{ pt: 3 }}>
                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label={t("ProductName")}
                            value={product.product_name}
                            onChange={(e) => setProduct({ ...product, product_name: e.target.value })}
                            margin="normal"
                            required
                        />

                        <Box sx={{ my: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('ProductDescription')}
                            </Typography>
                            <MDEditor
                                value={product.product_description}
                                onChange={(value) => setProduct({ ...product, product_description: value })}
                                preview="edit"
                                height={200}
                                visibleDragbar={false}
                            />
                        </Box>

                        <Box sx={{ my: 2 }}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="product-image-upload"
                                type="file"
                                onChange={handleImageChange}
                            />
                            <label htmlFor="product-image-upload">
                                <Button variant="outlined" component="span">
                                    {t('UploadProductImage')}
                                </Button>
                            </label>
                            <br />
                            <span>{t('ForBestViewUseDimension500x300')}</span>
                            {preview && (
                                <Avatar
                                    src={preview}
                                    variant="rounded"
                                    sx={{
                                        width: 250,
                                        height: 150,
                                        mt: 2,
                                        border: '2px dashed',
                                        borderColor: 'primary.main',
                                        objectFit: 'contain'
                                    }}
                                />
                            )}
                        </Box>

                        {errorMessage && (
                            <Typography color="error.main" sx={{ mt: 2 }}>
                                {errorMessage}
                            </Typography>
                        )}
                        {uploadErrorMessage && (
                            <Typography color="error.main" sx={{ mt: 2 }}>
                                {uploadErrorMessage}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
            }
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ mx: 1 }} color="secondary">
                    {t('Cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={uploading}
                    sx={{ mx: 1 }}
                >
                    {uploading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        mode === 'edit' ? t('UpdateMyProduct') : t('ListMyProduct')
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ProductForm;
ProductForm.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    uploading: PropTypes.bool,
    successMessage: PropTypes.string,
    user: PropTypes.shape({
        id: PropTypes.string.isRequired,
        role: PropTypes.string.isRequired
    }).isRequired,
    uploadLoading: PropTypes.bool,
    uploadError: PropTypes.string,
    errorMessage: PropTypes.string,
    mode: PropTypes.oneOf(['add', 'edit']).isRequired,
    product: PropTypes.shape({
        product_id: PropTypes.string,
        product_name: PropTypes.string,
        product_description: PropTypes.string,
        product_image: PropTypes.string
    })
};